import React, { useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { MMLCube } from '../utils/MMLParser';
import { animatedParcelTracker } from '../utils/AnimatedParcelTracker';

interface MMLCubeRendererProps {
  cube: MMLCube;
  onClick?: () => void;
  onHover?: () => void;
  onUnhover?: () => void;
  isSelected?: boolean;
}

const MMLCubeRenderer: React.FC<MMLCubeRendererProps> = ({
  cube,
  onClick,
  onHover,
  onUnhover,
  isSelected = false
}) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);
  const [time, setTime] = useState(0);

  // Handle animations
  useFrame((state) => {
    if (!meshRef.current || !cube.animations || cube.animations.length === 0) return;

    setTime(state.clock.getElapsedTime() * 1000);

    cube.animations.forEach(anim => {
      if (anim.attr === 'y') {
        const elapsedTime = (time - anim.startTime) % (anim.duration * 2 + anim.pingPongDelay * 2);
        let progress;
        let newY = 0;

        if (elapsedTime < anim.duration) {
          // Forward animation (subindo)
          progress = elapsedTime / anim.duration;
          newY = THREE.MathUtils.lerp(anim.start, anim.end, progress);
          meshRef.current!.position.y = newY;
        } else if (elapsedTime < anim.duration + anim.pingPongDelay) {
          // Hold at end position
          newY = anim.end;
          meshRef.current!.position.y = newY;
        } else if (elapsedTime < anim.duration * 2 + anim.pingPongDelay) {
          // Reverse animation (descendo)
          progress = (elapsedTime - anim.duration - anim.pingPongDelay) / anim.duration;
          newY = THREE.MathUtils.lerp(anim.end, anim.start, progress);
          meshRef.current!.position.y = newY;
        } else {
          // Hold at start position
          newY = anim.start;
          meshRef.current!.position.y = newY;
        }

        // Atualizar o rastreador de parcelas animadas
        animatedParcelTracker.registerParcel(cube, newY);
      }
    });
  });

  // Create material with hover and selection effects
  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color(cube.color),
    emissive: new THREE.Color(isSelected ? '#ffffff' : (hovered ? '#FFA940' : '#F7931A')),
    emissiveIntensity: isSelected ? 0.5 : (hovered ? 0.3 : 0.1),
    transparent: false, // Removida a transparência para evitar z-fighting
    metalness: 0.4,  // Ajustar o metalness para o tom laranja
    roughness: 0.3,  // Diminuir a rugosidade para dar mais brilho
    polygonOffset: true, // Adiciona um pequeno offset para evitar z-fighting
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 1,
  });

  // Create edges material for better visibility - apenas visível quando selecionado ou com hover
  const edgeMaterial = new THREE.LineBasicMaterial({
    color: isSelected ? '#ffffff' : '#FFA940',  // Cor mais clara e laranja para as bordas
    transparent: true,
    opacity: isSelected ? 0.8 : (hovered ? 0.7 : 0),  // Ocultar as bordas quando não estiver selecionado ou com hover
    linewidth: 1
  });

  return (
    <group position={[cube.x, cube.y, cube.z]}>
      <mesh
        ref={meshRef}
        onClick={(e) => {
          e.stopPropagation();
          onClick && onClick();
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
          onHover && onHover();
        }}
        onPointerOut={(e) => {
          e.stopPropagation();
          setHovered(false);
          onUnhover && onUnhover();
        }}
        castShadow
        receiveShadow
      >
        <boxGeometry args={[cube.width, cube.height, cube.depth]} />
        <primitive object={material} attach="material" />
      </mesh>

      {/* Add edges for better visibility */}
      <lineSegments>
        <edgesGeometry args={[new THREE.BoxGeometry(cube.width, cube.height, cube.depth)]} />
        <primitive object={edgeMaterial} attach="material" />
      </lineSegments>
    </group>
  );
};

export default MMLCubeRenderer;
